import { useState, useEffect } from 'react';
import api, { formatoMXN } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { TrendingUp, TrendingDown, Minus, BarChart3 } from 'lucide-react';

/** Componente compartido: muestra comparativa de los ultimos N meses
 * con ingresos, operaciones, ticket promedio y variacion % vs mes anterior.
 * Sirve para TallerPro y WashPro — el endpoint detecta `tipo_negocio` del tenant.
 */
export default function ComparativaMensual({ etiquetaOperaciones = 'Operaciones' }) {
  const [data, setData] = useState(null);
  const [meses, setMeses] = useState(6);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    setCargando(true);
    api.get(`/reportes/comparativa-mensual?meses=${meses}`)
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setCargando(false));
  }, [meses]);

  const nombreMes = (yyyymm) => {
    if (!yyyymm) return '';
    const [y, m] = yyyymm.split('-');
    const d = new Date(parseInt(y), parseInt(m) - 1, 1);
    return d.toLocaleDateString('es-MX', { month: 'short', year: '2-digit' });
  };

  const filas = (data?.meses || []).slice().reverse();
  const maxIngresos = Math.max(1, ...filas.map(f => f.ingresos));

  return (
    <Card className="bg-slate-900/40 border-slate-800" data-testid="comparativa-mensual">
      <CardHeader className="pb-3 flex flex-row items-center justify-between space-y-0">
        <CardTitle className="font-barlow text-lg text-slate-200 flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-cyan-400" /> Comparativo Mensual
        </CardTitle>
        <select value={meses} onChange={e => setMeses(parseInt(e.target.value))}
          className="bg-slate-950 border border-slate-700 text-slate-200 rounded-md h-8 text-xs px-2"
          data-testid="comparativa-meses-select">
          <option value={3}>Últimos 3 meses</option>
          <option value={6}>Últimos 6 meses</option>
          <option value={12}>Últimos 12 meses</option>
        </select>
      </CardHeader>
      <CardContent className="p-0">
        {cargando ? (
          <div className="p-8 text-center text-slate-500 text-sm">Cargando...</div>
        ) : filas.length === 0 ? (
          <div className="p-8 text-center text-slate-500 text-sm">Sin datos suficientes</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-800 text-[10px] uppercase tracking-wider text-slate-500">
                  <th className="text-left py-2 px-4">Mes</th>
                  <th className="text-right py-2 px-4">Ingresos</th>
                  <th className="text-right py-2 px-4">{etiquetaOperaciones}</th>
                  <th className="text-right py-2 px-4">Ticket prom.</th>
                  <th className="text-right py-2 px-4">Vs mes ant.</th>
                </tr>
              </thead>
              <tbody>
                {filas.map((f, i) => {
                  const pct = f.variacion_pct;
                  const positivo = pct !== null && pct > 0;
                  const negativo = pct !== null && pct < 0;
                  const barPct = Math.round((f.ingresos / maxIngresos) * 100);
                  return (
                    <tr key={f.mes} className={`border-b border-slate-800/50 ${i === 0 ? 'bg-cyan-500/5' : ''}`}
                      data-testid={`comparativa-fila-${f.mes}`}>
                      <td className="py-2.5 px-4">
                        <div className="text-slate-200 font-medium capitalize">{nombreMes(f.mes)}</div>
                        {i === 0 && <span className="text-[9px] text-cyan-400 uppercase tracking-wider">Actual</span>}
                      </td>
                      <td className="py-2.5 px-4 text-right">
                        <div className="text-slate-100 font-mono font-medium">{formatoMXN(f.ingresos)}</div>
                        <div className="mt-1 h-1 bg-slate-800 rounded-full overflow-hidden">
                          <div className="h-full bg-cyan-500/70" style={{ width: `${barPct}%` }} />
                        </div>
                      </td>
                      <td className="py-2.5 px-4 text-right text-slate-300 font-mono">{f.operaciones}</td>
                      <td className="py-2.5 px-4 text-right text-slate-400 font-mono">{formatoMXN(f.ticket_promedio)}</td>
                      <td className="py-2.5 px-4 text-right">
                        {pct === null ? (
                          <span className="text-slate-600 text-xs">—</span>
                        ) : (
                          <span className={`inline-flex items-center gap-0.5 text-xs font-medium ${
                            positivo ? 'text-emerald-400' : negativo ? 'text-red-400' : 'text-slate-400'
                          }`}>
                            {positivo && <TrendingUp className="w-3 h-3" />}
                            {negativo && <TrendingDown className="w-3 h-3" />}
                            {pct === 0 && <Minus className="w-3 h-3" />}
                            {Math.abs(pct)}%
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
